/**
 * Admin Backups — /admin/backups
 *
 * A simple ops dashboard for the automated database-backup pipeline.
 * Shows:
 *   • Latest backup status (green tick / red warning card).
 *   • "Run backup now" button — POSTs /api/admin/backups/run-now, blocks
 *     until the ZIP is generated + uploaded (usually <10s).
 *   • Full history table with per-row download button.
 *
 * Admin-only. Uses the same header/back-nav as AdminInvoicesPage.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, CheckCircle2, Download, HardDriveDownload, Loader2,
  Play, RefreshCw, ShieldAlert, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function humanBytes(n) {
  if (!n) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 0)} ${units[i]}`;
}

function humanDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60); const s = Math.round(seconds - m * 60);
  return `${m}m ${s}s`;
}

function relTime(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function AdminBackupsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'moderator') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/backups/list?limit=100`, {
        headers: authHeader, withCredentials: true,
      });
      setRows(res.data.backups || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load backups.');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setError('');
    try {
      await axios.post(`${API}/api/admin/backups/run-now`, {}, {
        headers: authHeader, withCredentials: true,
      });
      await load();
    } catch (err) {
      setError('Backup failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRunning(false);
    }
  };

  const downloadOne = async (row) => {
    setDownloading(row.id);
    try {
      const res = await axios.get(`${API}/api/admin/backups/download/${row.id}`, {
        headers: authHeader, withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = row.filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloading(null);
    }
  };

  const lastSuccess = rows.find(r => r.status === 'success');
  const lastAny = rows[0];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Dark header */}
      <div className="bg-slate-900 text-white px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white mb-4"
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
          </Button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <HardDriveDownload className="w-7 h-7" />
                Database Backups
              </h1>
              <p className="text-slate-300 mt-2 text-sm max-w-2xl">
                Automated ZIP backups of every collection, pushed to Emergent
                Object Storage nightly at 03:00 IST. Retention: keep everything
                forever. Every download is audit-logged.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={load}
                disabled={loading || running}
                data-testid="refresh-backups-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Refresh
              </Button>
              <Button
                onClick={runNow}
                disabled={running || loading}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="run-backup-now-btn"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                {running ? 'Backing up…' : 'Run backup now'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm"
               data-testid="backups-error">
            {error}
          </div>
        )}

        {/* Status card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {lastSuccess ? (
                <><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Latest successful backup</>
              ) : (
                <><ShieldAlert className="w-5 h-5 text-amber-500" /> No successful backup yet</>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lastSuccess ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wider">When</p>
                  <p className="font-semibold" data-testid="latest-backup-when">
                    {relTime(lastSuccess.started_at)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(lastSuccess.started_at).toLocaleString('en-IN')}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wider">Size</p>
                  <p className="font-semibold">{humanBytes(lastSuccess.size_bytes)}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wider">Documents</p>
                  <p className="font-semibold">{(lastSuccess.total_documents ?? 0).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wider">Source</p>
                  <p className="font-semibold capitalize">{lastSuccess.source || '—'}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                No successful backups yet. Click <b>Run backup now</b> above to make
                the first one, or wait for the 03:00 IST scheduled run.
              </p>
            )}
            {lastAny && lastAny.status !== 'success' && (
              <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <b>Last attempt at {new Date(lastAny.started_at).toLocaleString('en-IN')} {lastAny.status}.</b>
                {lastAny.error && <> Reason: {lastAny.error}</>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* History table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">History ({rows.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Filename</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-right px-4 py-2">Size</th>
                    <th className="text-right px-4 py-2">Docs</th>
                    <th className="text-right px-4 py-2">Duration</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="backups-history-tbody">
                  {rows.length === 0 && !loading && (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                        No backups yet. Click <b>Run backup now</b> above.
                      </td>
                    </tr>
                  )}
                  {rows.map(row => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-xs">{relTime(row.started_at)}</span>
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {new Date(row.started_at).toLocaleString('en-IN')}
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{row.filename}</td>
                      <td className="px-4 py-2">
                        {row.status === 'success' && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">OK</Badge>}
                        {row.status === 'in_progress' && <Badge className="bg-blue-100 text-blue-700 border border-blue-200">Running</Badge>}
                        {row.status === 'failed' && <Badge className="bg-red-100 text-red-700 border border-red-200">Failed</Badge>}
                      </td>
                      <td className="px-4 py-2 capitalize">{row.source || '—'}</td>
                      <td className="px-4 py-2 text-right">{humanBytes(row.size_bytes)}</td>
                      <td className="px-4 py-2 text-right">{(row.total_documents ?? '—').toLocaleString?.('en-IN') || '—'}</td>
                      <td className="px-4 py-2 text-right">{humanDuration(row.duration_seconds)}</td>
                      <td className="px-4 py-2 text-right">
                        {row.status === 'success' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadOne(row)}
                            disabled={downloading === row.id}
                            data-testid={`download-backup-${row.id}-btn`}
                          >
                            {downloading === row.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Download className="w-3 h-3" />}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
